/**
 * AudioSystem - Manages all game audio (sound effects and music)
 *
 * Features:
 * - Sound effects: shoot, enemy_death, soldier_multiply, weapon_unlock, game_over, level_complete, button_tap
 * - Background music: menu (calm), gameplay (upbeat), victory/game over stings
 * - Audio plays at START of visual action, never delayed
 * - Support mute toggles from settings via SaveSystem
 * - Generates placeholder audio using Web Audio API (can be replaced with real files later)
 * - Handles audio context unlock on first user interaction (mobile requirement)
 * - Crossfade between music tracks on scene transitions
 *
 * Per PRD section 7.1 and TASKS.md #28, #29
 */

import Phaser from 'phaser';
import { saveSystem } from './SaveSystem';
import { createAudioContextSafely } from './audio/contextBootstrap';
import { getMusicTrackVolume } from './audio/musicControl';
import { playGeneratedSound, SOUND_CONFIGS } from './audio/sfxControl';
import { MusicTrack, SoundKey } from './audio/types';
import { devLog, devWarn } from '../utils/devLog';

/**
 * AudioSystem singleton class
 * Manages all game audio with settings respect and mobile context handling
 */
export class AudioSystem {
  private static instance: AudioSystem | null = null;

  private audioContext: AudioContext | null = null;
  private isUnlocked: boolean = false;
  private scene: Phaser.Scene | null = null;
  private boundUnlockHandler: (() => void) | null = null;

  // Music state
  private currentTrack: MusicTrack | null = null;
  private musicGainNode: GainNode | null = null;
  private musicOscillators: OscillatorNode[] = [];
  private musicScheduler: number | null = null;
  private musicStartTime: number = 0;
  private isCrossfading: boolean = false;

  /**
   * Private constructor for singleton pattern
   */
  private constructor() {
    // Create audio context on first access
    this.initAudioContext();
  }

  /**
   * Gets the singleton instance
   */
  public static getInstance(): AudioSystem {
    if (!AudioSystem.instance) {
      AudioSystem.instance = new AudioSystem();
    }
    return AudioSystem.instance;
  }

  /**
   * Initializes the Web Audio API context
   * Handles browser compatibility
   */
  private initAudioContext(): void {
    this.audioContext = createAudioContextSafely();
    if (this.audioContext?.state === 'running') {
      this.isUnlocked = true;
    }
  }

  /**
   * Sets up unlock listener for mobile browsers
   * Must be called after Phaser scene is ready
   */
  public setupUnlockListener(scene: Phaser.Scene): void {
    this.scene = scene;

    if (this.isUnlocked || !this.audioContext) return;

    // Create unlock handler
    this.boundUnlockHandler = () => this.unlockAudioContext();

    // Listen for first user interaction
    scene.input.on('pointerdown', this.boundUnlockHandler);
    document.addEventListener('touchstart', this.boundUnlockHandler, { once: true });
    document.addEventListener('click', this.boundUnlockHandler, { once: true });
    document.addEventListener('keydown', this.boundUnlockHandler, { once: true });
  }

  /**
   * Unlocks the audio context on user interaction
   * Required for mobile browsers that suspend audio until interaction
   */
  private unlockAudioContext(): void {
    if (this.isUnlocked || !this.audioContext) return;

    // Resume the audio context
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume()
        .then(() => {
          this.isUnlocked = true;
          this.cleanupUnlockListeners();
          devLog('AudioSystem: Audio context unlocked');
        })
        .catch((error) => {
          devWarn('AudioSystem: Failed to unlock audio context:', error);
        });
    } else {
      this.isUnlocked = true;
      this.cleanupUnlockListeners();
    }
  }

  /**
   * Removes unlock event listeners after unlocking
   */
  private cleanupUnlockListeners(): void {
    if (!this.boundUnlockHandler) return;

    if (this.scene) {
      this.scene.input.off('pointerdown', this.boundUnlockHandler);
    }
    document.removeEventListener('touchstart', this.boundUnlockHandler);
    document.removeEventListener('click', this.boundUnlockHandler);
    document.removeEventListener('keydown', this.boundUnlockHandler);

    this.boundUnlockHandler = null;
  }

  /**
   * Plays a sound effect
   * Respects sound settings from SaveSystem
   *
   * @param key The sound effect key to play
   * @param volumeMultiplier Optional volume adjustment (0-1)
   */
  public play(key: SoundKey, volumeMultiplier: number = 1): void {
    // Check if sound is enabled in settings
    if (!saveSystem.isSoundEnabled()) {
      return;
    }

    // Check if audio context is ready
    if (!this.audioContext || this.audioContext.state !== 'running') {
      // Silently fail - audio not ready
      return;
    }

    // Get sound configuration
    const config = SOUND_CONFIGS[key];
    if (!config) {
      devWarn(`AudioSystem: Unknown sound key: ${key}`);
      return;
    }

    // Generate and play the sound
    playGeneratedSound(this.audioContext, config, volumeMultiplier);
  }

  /**
   * Plays a cascade of sounds (e.g., for soldier multiplication)
   *
   * @param key The sound effect key
   * @param count Number of sounds in cascade
   * @param intervalMs Interval between sounds in ms
   */
  public playCascade(key: SoundKey, count: number, intervalMs: number = 50): void {
    if (!saveSystem.isSoundEnabled()) return;

    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        // Decrease volume slightly for later sounds
        const volumeMultiplier = 1 - (i / count) * 0.3;
        this.play(key, volumeMultiplier);
      }, i * intervalMs);
    }
  }

  /**
   * Plays a musical phrase (for weapon unlock flourish)
   */
  public playUpgradeFlourish(): void {
    if (!saveSystem.isSoundEnabled()) return;
    if (!this.audioContext || this.audioContext.state !== 'running') return;

    // Play ascending notes: C5, E5, G5
    const notes = [523, 659, 784];
    const duration = 0.12;

    notes.forEach((freq, i) => {
      setTimeout(() => {
        playGeneratedSound(this.audioContext, {
          type: 'triangle',
          frequency: freq,
          duration: duration,
          volume: 0.25,
          ramp: 'both',
        }, 1);
      }, i * 100);
    });
  }

  /**
   * Plays level complete fanfare
   */
  public playLevelCompleteFanfare(): void {
    if (!saveSystem.isSoundEnabled()) return;
    if (!this.audioContext || this.audioContext.state !== 'running') return;

    // Play victory chord: C5, E5, G5, C6
    const notes = [523, 659, 784, 1047];

    notes.forEach((freq, i) => {
      setTimeout(() => {
        playGeneratedSound(this.audioContext, {
          type: 'triangle',
          frequency: freq,
          duration: 0.4 - i * 0.05,
          volume: 0.2,
          ramp: 'both',
        }, 1);
      }, i * 80);
    });
  }

  /**
   * Plays game over sound sequence
   */
  public playGameOverSequence(): void {
    if (!saveSystem.isSoundEnabled()) return;
    if (!this.audioContext || this.audioContext.state !== 'running') return;

    // Descending sad notes
    const notes = [400, 350, 300];

    notes.forEach((freq, i) => {
      setTimeout(() => {
        playGeneratedSound(this.audioContext, {
          type: 'sawtooth',
          frequency: freq,
          duration: 0.3,
          volume: 0.15,
          ramp: 'down',
        }, 1);
      }, i * 200);
    });
  }

  /**
   * Checks if sound is currently enabled
   */
  public isSoundEnabled(): boolean {
    return saveSystem.isSoundEnabled();
  }

  /**
   * Checks if music is currently enabled
   */
  public isMusicEnabled(): boolean {
    return saveSystem.isMusicEnabled();
  }

  /**
   * Toggles sound effects on/off
   */
  public toggleSound(): boolean {
    const newValue = !saveSystem.isSoundEnabled();
    saveSystem.updateSettings({ soundEnabled: newValue });

    // Play feedback sound if enabling
    if (newValue) {
      this.play('button_tap');
    }

    return newValue;
  }

  /**
   * Toggles music on/off
   * Note: Music playback is implemented in Task #29
   */
  public toggleMusic(): boolean {
    const newValue = !saveSystem.isMusicEnabled();
    saveSystem.updateSettings({ musicEnabled: newValue });
    return newValue;
  }

  /**
   * Sets sound enabled state directly
   */
  public setSoundEnabled(enabled: boolean): void {
    saveSystem.updateSettings({ soundEnabled: enabled });
  }

  /**
   * Sets music enabled state directly
   */
  public setMusicEnabled(enabled: boolean): void {
    saveSystem.updateSettings({ musicEnabled: enabled });

    // Stop or resume music based on new state
    if (enabled && this.currentTrack) {
      this.playMusic(this.currentTrack);
    } else if (!enabled) {
      this.stopMusic(true);
    }
  }

  // ===========================================================================
  // MUSIC PLAYBACK
  // ===========================================================================

  /**
   * Plays a music track with optional crossfade
   * @param track The track to play
   * @param crossfade Whether to crossfade from current track (default: true)
   */
  public playMusic(track: MusicTrack, crossfade: boolean = true): void {
    if (!saveSystem.isMusicEnabled()) {
      this.currentTrack = track; // Remember for when music is enabled
      return;
    }

    if (!this.audioContext || this.audioContext.state !== 'running') {
      this.currentTrack = track;
      return;
    }

    // Don't restart if same track is playing
    if (this.currentTrack === track && this.musicGainNode) {
      return;
    }

    // If crossfading, fade out current then start new
    if (crossfade && this.musicGainNode && !this.isCrossfading) {
      this.isCrossfading = true;
      const crossfadeDuration = 0.5;

      // Fade out current
      this.musicGainNode.gain.linearRampToValueAtTime(
        0,
        this.audioContext.currentTime + crossfadeDuration
      );

      // Schedule new track after fade
      setTimeout(() => {
        this.stopMusicImmediate();
        this.startMusicTrack(track);
        this.isCrossfading = false;
      }, crossfadeDuration * 1000);
    } else {
      this.stopMusicImmediate();
      this.startMusicTrack(track);
    }

    this.currentTrack = track;
  }

  /**
   * Stops current music
   * @param immediate If true, stops immediately without fade
   */
  public stopMusic(immediate: boolean = false): void {
    if (!this.musicGainNode || !this.audioContext) {
      this.stopMusicImmediate();
      return;
    }

    if (immediate) {
      this.stopMusicImmediate();
    } else {
      // Fade out over 0.3 seconds
      const fadeTime = 0.3;
      this.musicGainNode.gain.linearRampToValueAtTime(
        0,
        this.audioContext.currentTime + fadeTime
      );

      setTimeout(() => this.stopMusicImmediate(), fadeTime * 1000);
    }
  }

  /**
   * Immediately stops all music without fade
   */
  private stopMusicImmediate(): void {
    // Clear scheduler
    if (this.musicScheduler !== null) {
      clearInterval(this.musicScheduler);
      this.musicScheduler = null;
    }

    // Stop all oscillators
    this.musicOscillators.forEach((osc) => {
      try {
        osc.stop();
        osc.disconnect();
      } catch {
        // Ignore errors from already stopped oscillators
      }
    });
    this.musicOscillators = [];

    // Disconnect gain node
    if (this.musicGainNode) {
      try {
        this.musicGainNode.disconnect();
      } catch {
        // Ignore
      }
      this.musicGainNode = null;
    }
  }

  /**
   * Starts playing a specific music track
   */
  private startMusicTrack(track: MusicTrack): void {
    if (!this.audioContext) return;

    // Create master gain for music
    this.musicGainNode = this.audioContext.createGain();
    this.musicGainNode.gain.value = 0;
    this.musicGainNode.connect(this.audioContext.destination);

    // Fade in
    this.musicGainNode.gain.linearRampToValueAtTime(
      this.getMusicVolume(track),
      this.audioContext.currentTime + 0.3
    );

    this.musicStartTime = this.audioContext.currentTime;

    // Start the appropriate music generator
    switch (track) {
      case 'menu':
        this.playMenuMusic();
        break;
      case 'gameplay':
        this.playGameplayMusic();
        break;
      case 'victory':
        this.playVictorySting();
        break;
      case 'gameover':
        this.playGameOverSting();
        break;
    }
  }

  /**
   * Returns the volume for a given track type
   */
  private getMusicVolume(track: MusicTrack): number {
    return getMusicTrackVolume(track);
  }

  /**
   * Plays calm menu background music
   * Simple ambient pad with slow chord changes
   */
  private playMenuMusic(): void {
    if (!this.audioContext || !this.musicGainNode) return;

    const ctx = this.audioContext;

    // Chord progression: Am - F - C - G (frequencies for root notes)
    // 8 second loop: 4 chords × 2 seconds each
    const chords = [
      [220, 277, 330], // Am
      [175, 220, 262], // F
      [262, 330, 392], // C
      [196, 247, 294], // G
    ];

    const scheduleChord = () => {
      if (!this.musicGainNode || !this.audioContext) return;

      const now = ctx.currentTime;
      const elapsed = now - this.musicStartTime;
      const chordIndex = Math.floor((elapsed / 2) % 4);
      const chord = chords[chordIndex];

      chord.forEach((freq) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.value = freq;

        // Soft envelope
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.15, now + 0.3);
        gain.gain.linearRampToValueAtTime(0.1, now + 1.5);
        gain.gain.linearRampToValueAtTime(0, now + 2);

        osc.connect(gain);
        gain.connect(this.musicGainNode!);

        osc.start(now);
        osc.stop(now + 2.1);

        this.musicOscillators.push(osc);

        osc.onended = () => {
          const idx = this.musicOscillators.indexOf(osc);
          if (idx > -1) this.musicOscillators.splice(idx, 1);
          osc.disconnect();
          gain.disconnect();
        };
      });
    };

    // Schedule initial chord and repeat
    scheduleChord();
    this.musicScheduler = window.setInterval(scheduleChord, 2000);
  }

  /**
   * Plays upbeat gameplay music
   * More energetic with bass and rhythm
   */
  private playGameplayMusic(): void {
    if (!this.audioContext || !this.musicGainNode) return;

    const ctx = this.audioContext;
    const bpm = 120;
    const beatDuration = 60 / bpm;

    // Bass notes for a driving feel
    const bassNotes = [110, 110, 147, 131]; // A2, A2, D3, C3

    let beatCount = 0;

    const scheduleBeat = () => {
      if (!this.musicGainNode || !this.audioContext) return;

      const now = ctx.currentTime;
      const noteIndex = beatCount % bassNotes.length;
      const bassFreq = bassNotes[noteIndex];

      // Bass hit
      const bassOsc = ctx.createOscillator();
      const bassGain = ctx.createGain();

      bassOsc.type = 'triangle';
      bassOsc.frequency.value = bassFreq;

      bassGain.gain.setValueAtTime(0.2, now);
      bassGain.gain.exponentialRampToValueAtTime(0.01, now + beatDuration * 0.8);

      bassOsc.connect(bassGain);
      bassGain.connect(this.musicGainNode!);

      bassOsc.start(now);
      bassOsc.stop(now + beatDuration);

      this.musicOscillators.push(bassOsc);

      bassOsc.onended = () => {
        const idx = this.musicOscillators.indexOf(bassOsc);
        if (idx > -1) this.musicOscillators.splice(idx, 1);
        bassOsc.disconnect();
        bassGain.disconnect();
      };

      // Add a hi-hat style click on off-beats
      if (beatCount % 2 === 1) {
        const hihatOsc = ctx.createOscillator();
        const hihatGain = ctx.createGain();

        hihatOsc.type = 'square';
        hihatOsc.frequency.value = 800;

        hihatGain.gain.setValueAtTime(0.03, now);
        hihatGain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

        hihatOsc.connect(hihatGain);
        hihatGain.connect(this.musicGainNode!);

        hihatOsc.start(now);
        hihatOsc.stop(now + 0.06);

        this.musicOscillators.push(hihatOsc);

        hihatOsc.onended = () => {
          const idx = this.musicOscillators.indexOf(hihatOsc);
          if (idx > -1) this.musicOscillators.splice(idx, 1);
          hihatOsc.disconnect();
          hihatGain.disconnect();
        };
      }

      // Add melody note every 4 beats
      if (beatCount % 4 === 0) {
        const melodyNotes = [440, 523, 587, 523]; // A4, C5, D5, C5
        const melodyFreq = melodyNotes[(beatCount / 4) % melodyNotes.length];

        const melodyOsc = ctx.createOscillator();
        const melodyGain = ctx.createGain();

        melodyOsc.type = 'sine';
        melodyOsc.frequency.value = melodyFreq;

        melodyGain.gain.setValueAtTime(0.08, now);
        melodyGain.gain.linearRampToValueAtTime(0.05, now + beatDuration * 2);
        melodyGain.gain.linearRampToValueAtTime(0, now + beatDuration * 3);

        melodyOsc.connect(melodyGain);
        melodyGain.connect(this.musicGainNode!);

        melodyOsc.start(now);
        melodyOsc.stop(now + beatDuration * 3.1);

        this.musicOscillators.push(melodyOsc);

        melodyOsc.onended = () => {
          const idx = this.musicOscillators.indexOf(melodyOsc);
          if (idx > -1) this.musicOscillators.splice(idx, 1);
          melodyOsc.disconnect();
          melodyGain.disconnect();
        };
      }

      beatCount++;
    };

    // Start immediately and repeat
    scheduleBeat();
    this.musicScheduler = window.setInterval(scheduleBeat, beatDuration * 1000);
  }

  /**
   * Plays a short victory celebration sting
   */
  private playVictorySting(): void {
    if (!this.audioContext || !this.musicGainNode) return;

    const ctx = this.audioContext;
    const now = ctx.currentTime;

    // Triumphant ascending arpeggio: C-E-G-C (octave up)
    const notes = [
      { freq: 523, time: 0 },     // C5
      { freq: 659, time: 0.12 },  // E5
      { freq: 784, time: 0.24 },  // G5
      { freq: 1047, time: 0.36 }, // C6
      { freq: 1319, time: 0.48 }, // E6
    ];

    notes.forEach(({ freq, time }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.value = freq;

      gain.gain.setValueAtTime(0, now + time);
      gain.gain.linearRampToValueAtTime(0.2, now + time + 0.05);
      gain.gain.linearRampToValueAtTime(0.1, now + time + 0.3);
      gain.gain.linearRampToValueAtTime(0, now + time + 0.6);

      osc.connect(gain);
      gain.connect(this.musicGainNode!);

      osc.start(now + time);
      osc.stop(now + time + 0.7);

      this.musicOscillators.push(osc);

      osc.onended = () => {
        const idx = this.musicOscillators.indexOf(osc);
        if (idx > -1) this.musicOscillators.splice(idx, 1);
        osc.disconnect();
        gain.disconnect();
      };
    });

    // Final chord
    const chordNotes = [523, 659, 784, 1047];
    chordNotes.forEach((freq) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.value = freq;

      const startTime = now + 0.6;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.15, startTime + 0.1);
      gain.gain.linearRampToValueAtTime(0.08, startTime + 1);
      gain.gain.linearRampToValueAtTime(0, startTime + 2);

      osc.connect(gain);
      gain.connect(this.musicGainNode!);

      osc.start(startTime);
      osc.stop(startTime + 2.1);

      this.musicOscillators.push(osc);

      osc.onended = () => {
        const idx = this.musicOscillators.indexOf(osc);
        if (idx > -1) this.musicOscillators.splice(idx, 1);
        osc.disconnect();
        gain.disconnect();
      };
    });

    // Clear track after sting completes
    setTimeout(() => {
      if (this.currentTrack === 'victory') {
        this.currentTrack = null;
      }
    }, 2500);
  }

  /**
   * Plays a brief game over sting
   */
  private playGameOverSting(): void {
    if (!this.audioContext || !this.musicGainNode) return;

    const ctx = this.audioContext;
    const now = ctx.currentTime;

    // Descending minor notes: E-D-C-B (sad descending)
    const notes = [
      { freq: 330, time: 0 },     // E4
      { freq: 294, time: 0.2 },   // D4
      { freq: 262, time: 0.4 },   // C4
      { freq: 247, time: 0.6 },   // B3
    ];

    notes.forEach(({ freq, time }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sawtooth';
      osc.frequency.value = freq;

      gain.gain.setValueAtTime(0, now + time);
      gain.gain.linearRampToValueAtTime(0.12, now + time + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, now + time + 0.4);

      osc.connect(gain);
      gain.connect(this.musicGainNode!);

      osc.start(now + time);
      osc.stop(now + time + 0.5);

      this.musicOscillators.push(osc);

      osc.onended = () => {
        const idx = this.musicOscillators.indexOf(osc);
        if (idx > -1) this.musicOscillators.splice(idx, 1);
        osc.disconnect();
        gain.disconnect();
      };
    });

    // Final low drone
    const droneOsc = ctx.createOscillator();
    const droneGain = ctx.createGain();

    droneOsc.type = 'sine';
    droneOsc.frequency.value = 110; // A2

    const droneStart = now + 0.8;
    droneGain.gain.setValueAtTime(0, droneStart);
    droneGain.gain.linearRampToValueAtTime(0.1, droneStart + 0.1);
    droneGain.gain.linearRampToValueAtTime(0, droneStart + 1.5);

    droneOsc.connect(droneGain);
    droneGain.connect(this.musicGainNode!);

    droneOsc.start(droneStart);
    droneOsc.stop(droneStart + 1.6);

    this.musicOscillators.push(droneOsc);

    droneOsc.onended = () => {
      const idx = this.musicOscillators.indexOf(droneOsc);
      if (idx > -1) this.musicOscillators.splice(idx, 1);
      droneOsc.disconnect();
      droneGain.disconnect();
    };

    // Clear track after sting completes
    setTimeout(() => {
      if (this.currentTrack === 'gameover') {
        this.currentTrack = null;
      }
    }, 2500);
  }

  /**
   * Gets the current playing track (or null if none)
   */
  public getCurrentTrack(): MusicTrack | null {
    return this.currentTrack;
  }

  /**
   * Cleanup method - call when game is destroyed
   */
  public destroy(): void {
    this.cleanupUnlockListeners();
    this.stopMusicImmediate();

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => {
        // Ignore close errors
      });
    }

    this.audioContext = null;
    this.scene = null;
    this.currentTrack = null;
    AudioSystem.instance = null;
  }

  /**
   * Gets whether the audio context is unlocked and ready
   */
  public isReady(): boolean {
    return this.isUnlocked && this.audioContext?.state === 'running';
  }
}

// Export singleton instance getter
export const audioSystem = AudioSystem.getInstance();
